import { ExtractComponentProp } from '@approvalmax/types';
import { Button, WarningImage } from '@approvalmax/ui';
import { Checkbox, Flex, Link, openSplitViewState, Popup, Spacing } from '@approvalmax/ui/src/components';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { domain } from 'modules/data';
import { cacheStoragePrefixAmaxPayPaymentDetails, clearCacheStorage } from 'modules/data-providers';
import { BeneficiaryStatusLegend } from 'modules/request/components/xero/BeneficiaryStatusLegend/BeneficiaryStatusLegend';
import { updateXeroAmaxPayBPDetails } from 'pages/requestForm/actions';
import { FC, Fragment, memo, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useToggle } from 'react-use';
import {
    EditCounterpartyPaymentDetailsPopupContent,
    EditPaymentDetailsPopupContent,
    splitViewGoToBarDefaultSettings,
    splitViewGoToBarSettingsState,
} from 'shared/components';
import { useGetBatchPaymentsRequestAmounts } from 'shared/data/webApp/v2';
import { isPayAvailableViaLicense } from 'shared/helpers';
import { useCompanyBetaFeatures } from 'shared/hooks';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';
import { usePermissions } from 'shared/hooks/usePermissions';
import { activeAttachmentIdState, ContentSplitViewMode, contentSplitViewState } from 'shared/states';

import { useXeroAmaxPayBatchPaymentFormContext } from '../../XeroAmaxPayBatchPaymentForm.context';
import { getColumnDefinitions } from './BillsInformation.config';
import { useMergedBySupplierItems, useMergedBySupplierListener } from './BillsInformation.hooks';
import { messages } from './BillsInformation.messages';
import { EmptyState, EmptyText, Root, StyledLineItemsTable, TableContainer } from './BillsInformation.styles';
import {
    BillsInformationProps,
    ContactReference,
    OnOpenAddPaymentDetailsPopupParams,
    OnSelectAttachmentParams,
} from './BillsInformation.types';
import { LineItemsTableHeaderForSupplier } from './components/LineItemsTableHeaderForSupplier/LineItemsTableHeaderForSupplier';

export const BillsInformation: FC<BillsInformationProps> = memo((props) => {
    const {
        toggleIsOpenSelectBankAccountPopup,
        request,
        hasSelectedBankAccount,
        paymentDetailsDiscrepanciesOnChecking,
    } = props;

    const dispatch = useDispatch();
    const [isAddPaymentDetailsPopupOpened, toggleAddPaymentDetailsPopupOpened] = useToggle(false);
    const [billIdForAddPaymentDetailsPopup, setBillIdForAddPaymentDetailsPopup] = useState('');
    const [contactReference, setContactReference] = useState<ContactReference | null>(null);

    const company = useCompanyBasedOnUrl();

    const { isPaymentDetailsAddEnabled } = usePermissions(company);
    const { isConfirmationOfPayee, isWalletsUK } = useCompanyBetaFeatures(company.id);

    const {
        billBatchPayment,
        removeSelectableBillById,
        changeBillInvoiceItemField,
        changePaymentDetails,
        showErrors,
        billInvoiceRequestIdsWithError,
        billInvoiceRequestIdsWithExceededAmount,
        changeReferencesForMergedItems,
        changeIsMergedBySupplier,
        referencesForMergedItems,
    } = useXeroAmaxPayBatchPaymentFormContext();

    const { isMergedBySupplier } = billBatchPayment;

    const isMergedBySupplierEnabled =
        company.betaFeatures.includes(domain.CompanyBetaFeature.PayMergedBySupplier) ||
        isPayAvailableViaLicense(company);

    const { items } = billBatchPayment;

    const setOpenSplitView = useSetAtom(openSplitViewState);
    const [contentSplitView, setContentSplitView] = useAtom(contentSplitViewState);
    const setSplitViewGoToBarSettings = useSetAtom(splitViewGoToBarSettingsState);
    const activeAttachmentId = useAtomValue(activeAttachmentIdState);

    const { mergedBySupplierItems, itemsCanBeMerged } = useMergedBySupplierItems(
        items,
        paymentDetailsDiscrepanciesOnChecking
    );

    useMergedBySupplierListener(itemsCanBeMerged);

    const { data: batchPaymentsRequestAmounts, isFetching: isFetchingGetBatchPaymentsRequestAmounts } =
        useGetBatchPaymentsRequestAmounts({
            path: {
                companyId: company.id,
                requestId: request.id,
            },
        });

    const onOpenAddPaymentDetailsPopup = useCallback(
        ({ contactId, contactName, billId }: OnOpenAddPaymentDetailsPopupParams) => {
            toggleAddPaymentDetailsPopupOpened(true);
            setBillIdForAddPaymentDetailsPopup(billId);
            setContactReference({ contactId, contactName });

            return Promise.resolve(null);
        },
        [toggleAddPaymentDetailsPopupOpened]
    );

    const onPaymentDetailsChanged = useCallback<
        ExtractComponentProp<typeof EditPaymentDetailsPopupContent, 'onPaymentDetailsChanged'>
    >(
        (paymentDetails) => {
            if (paymentDetails) {
                changePaymentDetails(billIdForAddPaymentDetailsPopup, paymentDetails);
            }

            toggleAddPaymentDetailsPopupOpened(false);
            setBillIdForAddPaymentDetailsPopup('');
            setContactReference(null);

            dispatch(
                clearCacheStorage((storageId) => String(storageId).startsWith(cacheStoragePrefixAmaxPayPaymentDetails))
            );
        },
        [billIdForAddPaymentDetailsPopup, changePaymentDetails, dispatch, toggleAddPaymentDetailsPopupOpened]
    );

    const onSelectAttachment = useCallback(
        ({ attachmentId, xeroBatchPaymentInvoiceId, xeroBatchPaymentInvoiceName }: OnSelectAttachmentParams) => {
            dispatch(updateXeroAmaxPayBPDetails(billBatchPayment));

            setOpenSplitView(true);
            setContentSplitView({
                mode: ContentSplitViewMode.AttachmentsToBillLinkedToBatchPayment,
                attachmentId,
                billId: xeroBatchPaymentInvoiceId,
                billFriendlyName: xeroBatchPaymentInvoiceName,
            });
            setSplitViewGoToBarSettings({
                show: true,
                targetContent: {
                    mode: ContentSplitViewMode.BillLinkedToBatchPayment,
                    billId: xeroBatchPaymentInvoiceId,
                },
                targetContentName: xeroBatchPaymentInvoiceName,
            });
        },
        [dispatch, billBatchPayment, setOpenSplitView, setContentSplitView, setSplitViewGoToBarSettings]
    );

    const openBill = useCallback(
        (xeroBatchPaymentInvoiceId: string) => {
            setContentSplitView({
                mode: ContentSplitViewMode.BillLinkedToBatchPayment,
                billId: xeroBatchPaymentInvoiceId,
            });
            setOpenSplitView(true);
        },
        [setContentSplitView, setOpenSplitView]
    );

    const handleRemoveSelectableBillById = useCallback(
        (xeroBillInvoiceRequestId: string) => {
            removeSelectableBillById(xeroBillInvoiceRequestId);

            const isBillInSplitView =
                contentSplitView.mode === ContentSplitViewMode.BillLinkedToBatchPayment &&
                contentSplitView.billId === xeroBillInvoiceRequestId;
            const isAttachmentOfBillInSplitView =
                contentSplitView.mode === ContentSplitViewMode.AttachmentsToBillLinkedToBatchPayment &&
                contentSplitView.billId === xeroBillInvoiceRequestId;

            if (openSplitViewState && (isBillInSplitView || isAttachmentOfBillInSplitView)) {
                setOpenSplitView(false);
                setContentSplitView({ mode: ContentSplitViewMode.Empty });
                setSplitViewGoToBarSettings(splitViewGoToBarDefaultSettings);
            }
        },
        [contentSplitView, removeSelectableBillById, setContentSplitView, setSplitViewGoToBarSettings, setOpenSplitView]
    );

    const columnDefinitions = useMemo(
        () =>
            getColumnDefinitions({
                removeSelectableBillById: handleRemoveSelectableBillById,
                changeBillInvoiceItemField,
                changePaymentDetails,
                showErrors,
                billInvoiceRequestIdsWithError,
                billInvoiceRequestIdsWithExceededAmount,
                onSelectAttachment,
                openBill,
                activeAttachmentId,
                onOpenAddPaymentDetailsPopup,
                isPaymentDetailsAddEnabled,
                paymentDetailsDiscrepanciesOnChecking,
                isMergedBySupplier,
                amounts: batchPaymentsRequestAmounts?.items ?? [],
                isAmountsLoading: isFetchingGetBatchPaymentsRequestAmounts,
            }),
        [
            handleRemoveSelectableBillById,
            changeBillInvoiceItemField,
            changePaymentDetails,
            showErrors,
            billInvoiceRequestIdsWithError,
            billInvoiceRequestIdsWithExceededAmount,
            onSelectAttachment,
            openBill,
            activeAttachmentId,
            onOpenAddPaymentDetailsPopup,
            isPaymentDetailsAddEnabled,
            paymentDetailsDiscrepanciesOnChecking,
            isMergedBySupplier,
            batchPaymentsRequestAmounts,
            isFetchingGetBatchPaymentsRequestAmounts,
        ]
    );

    return (
        <Root>
            {(!items.length || !hasSelectedBankAccount) && (
                <EmptyState>
                    <WarningImage />

                    {hasSelectedBankAccount ? (
                        <EmptyText>{messages.emptyText}</EmptyText>
                    ) : (
                        <>
                            <EmptyText>{messages.selectBankAccount}</EmptyText>

                            <Button execute={toggleIsOpenSelectBankAccountPopup}>{messages.addBankAccount}</Button>
                        </>
                    )}

                    <Spacing height={20} />
                </EmptyState>
            )}

            {!!items.length && (
                <>
                    <BeneficiaryStatusLegend redText={messages.paymentDetailsChanged} />

                    <Spacing height={16} />

                    {isMergedBySupplierEnabled && (
                        <Flex direction='row' alignItems='center' spacing='8'>
                            <Checkbox
                                checked={isMergedBySupplier}
                                onChange={changeIsMergedBySupplier}
                                disabled={!itemsCanBeMerged}
                            >
                                {messages.mergeBySupplier}
                            </Checkbox>

                            <Link
                                external
                                font='label'
                                fontSize='medium'
                                textAlign='center'
                                href='https://support.approvalmax.com/portal/en/kb/articles/how-to-merge-identical-supplier-bills-into-a-single-transaction'
                            >
                                {messages.learnMore}
                            </Link>
                        </Flex>
                    )}

                    {isMergedBySupplier ? (
                        mergedBySupplierItems.map((mergedBySupplier) => (
                            <Fragment key={mergedBySupplier.total.contact.id}>
                                <LineItemsTableHeaderForSupplier
                                    mergedBySupplierTotal={mergedBySupplier.total}
                                    name={messages.mergeBySupplierItemHeader({
                                        contactName: mergedBySupplier.total.contact.text,
                                    })}
                                    companyId={request.companyId}
                                    requestId={request.id}
                                    changeReferencesForMergedItems={changeReferencesForMergedItems}
                                    reference={
                                        referencesForMergedItems[
                                            `${mergedBySupplier.total.contact.id},${mergedBySupplier.total.paymentDetailsId}`
                                        ]
                                    }
                                    showErrors={showErrors}
                                    paymentDetailsDiscrepanciesOnChecking={paymentDetailsDiscrepanciesOnChecking}
                                />

                                <TableContainer>
                                    <StyledLineItemsTable
                                        columnDefinitions={columnDefinitions}
                                        lineItems={mergedBySupplier.items}
                                        paddingTop='small'
                                    />
                                </TableContainer>
                            </Fragment>
                        ))
                    ) : (
                        <TableContainer>
                            <StyledLineItemsTable columnDefinitions={columnDefinitions} lineItems={items} />
                        </TableContainer>
                    )}
                </>
            )}

            <Popup
                preventAutoClose
                open={isAddPaymentDetailsPopupOpened}
                onToggle={toggleAddPaymentDetailsPopupOpened}
                size='medium'
                returnFocus={false}
            >
                {isWalletsUK || isConfirmationOfPayee ? (
                    <EditCounterpartyPaymentDetailsPopupContent />
                ) : (
                    <EditPaymentDetailsPopupContent
                        onPaymentDetailsChanged={onPaymentDetailsChanged}
                        contactQueryData={{ id: contactReference?.contactId, name: contactReference?.contactName }}
                        requestId={request.id}
                    />
                )}
            </Popup>
        </Root>
    );
});

BillsInformation.displayName = 'BillsInformation';
